import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { axiosDelete, axiosGet, axiosPost } from 'Redux/axiosHelper'
import {
  GenerateMcpTokenPayload,
  McpConnectionInfo,
  McpTokenRow,
} from 'types/mcpToken/mcpToken'

/**
 * AI assistant connections for the signed-in user.
 *
 * Everything here is scoped to the caller by the API — there is no organization parameter and no way
 * to see anyone else's connections, so these need no ids passed in.
 */

export const getMcpConnectionInfo = async (): Promise<McpConnectionInfo> => {
  const res = await axiosGet(`v1/mcp-tokens/connection`)
  return res.data
}

export const useMcpConnectionInfo = () =>
  useQuery<McpConnectionInfo>(['mcp-connection'], getMcpConnectionInfo, {
    // The URL and the environment's capabilities do not change while someone is on the screen.
    staleTime: Infinity,
    retry: false,
  })

export const getMcpTokens = async (): Promise<McpTokenRow[]> => {
  const res = await axiosGet(`v1/mcp-tokens`)
  return res.data
}

export const useMcpTokens = () => useQuery<McpTokenRow[]>(['mcp-tokens'], getMcpTokens)

export const generateMcpToken = async (
  payload: GenerateMcpTokenPayload,
): Promise<McpTokenRow> => {
  // `showMessage: false` — axiosPost pops a generic "Data Added Successfully!" toast. The screen
  // shows the token itself, which is the only feedback that matters here.
  const res = await axiosPost(payload, `v1/mcp-tokens`, undefined, false)
  return res.data
}

export const useGenerateMcpToken = () => {
  const queryClient = useQueryClient()
  return useMutation(generateMcpToken, {
    onSuccess: () => {
      queryClient.invalidateQueries(['mcp-tokens'])
    },
  })
}

export const revokeMcpToken = async (tokenId: string) => {
  const res = await axiosDelete(`v1/mcp-tokens/${tokenId}`)
  return res.data
}

export const useRevokeMcpToken = () => {
  const queryClient = useQueryClient()
  return useMutation(revokeMcpToken, {
    onSuccess: () => {
      queryClient.invalidateQueries(['mcp-tokens'])
    },
  })
}
